import React, { useState, useEffect } from 'react';
import {
  Paper, Title, Text, Group, Badge, Button, Collapse, UnstyledButton, Divider, Alert, TextInput,
  Radio, Select, Table, Stack, SegmentedControl, CopyButton, ActionIcon, Tooltip
} from '@mantine/core';
import { IconChevronDown, IconCopy, IconCheck, IconX, IconUsers } from '@tabler/icons-react';
import MusicUpload from './MusicUpload/MusicUpload';
import { FOOTWEAR_OPTIONS } from '../config/categories';

const inputStyles = { label: { textAlign: 'left' }, description: { textAlign: 'left' } };

const ENSEMBLE_LENGTHS = ['2 minutes', '2.5 minutes', '3 minutes', '3.5 minutes', '4 minutes', '4.5 minutes', '5 minutes']
  .map(v => ({ value: v, label: v }));

const footwearLabel = (value) => FOOTWEAR_OPTIONS.find(o => o.value === value)?.label || '—';

export const inviteLink = (code) => `${window.location.origin}/register/individual/step-3?team=${encodeURIComponent(code)}`;

/**
 * Footwear + "start a team" / "join with a code". Used for a new ensemble
 * entry and for a "join later" entry that isn't on a team yet.
 */
export const EnsembleJoinForm = ({ initialMode = 'start', initialCode = '', initialFootwear = '', onStart, onJoin, onLater, busy }) => {
  const [mode, setMode] = useState(initialCode ? 'join' : initialMode);
  const [footwear, setFootwear] = useState(initialFootwear || '');
  const [teamName, setTeamName] = useState('');
  const [code, setCode] = useState(initialCode);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (mode !== 'later' && !footwear) return setError('Please choose pointe or soft shoes.');
    try {
      if (mode === 'start') {
        if (!teamName.trim()) return setError('Please give your team a name.');
        await onStart(teamName.trim(), footwear);
      } else if (mode === 'join') {
        if (!code.trim()) return setError("Enter your team's code, e.g. PDC-7KQ3.");
        await onJoin(code.trim(), footwear);
      } else {
        await onLater(footwear || null);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const modes = [
    { value: 'start', label: 'Start a team' },
    { value: 'join', label: 'Join with a code' },
    ...(onLater ? [{ value: 'later', label: 'Join later' }] : []),
  ];

  return (
    <Stack gap="md">
      <SegmentedControl fullWidth value={mode} onChange={setMode} data={modes} />
      {mode === 'start' && (
        <TextInput
          label="Team name"
          placeholder="e.g. Swan Sisters"
          description="You'll be the team lead and get a code to share with your teammates."
          value={teamName}
          onChange={(e) => setTeamName(e.target.value)}
          styles={inputStyles}
        />
      )}
      {mode === 'join' && (
        <TextInput
          label="Team code"
          placeholder="PDC-7KQ3"
          description="Ask your team lead for the code."
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          styles={inputStyles}
        />
      )}
      {mode === 'later' && (
        <Text size="sm" style={{ textAlign: 'left' }}>
          Reserve an ensemble spot now — you can pay for it (and get the solo + ensemble deal) and join a team when you're ready.
        </Text>
      )}
      <Radio.Group
        label="Your footwear in this ensemble"
        value={footwear}
        onChange={setFootwear}
        required={mode !== 'later'}
        styles={inputStyles}
      >
        <Group mt="xs" gap="xl">
          {FOOTWEAR_OPTIONS.map(o => <Radio key={o.value} value={o.value} label={o.label} />)}
        </Group>
      </Radio.Group>
      {error && <Alert color="red" p="xs"><Text size="sm" style={{ textAlign: 'left' }}>{error}</Text></Alert>}
      <Group justify="flex-end">
        <Button onClick={submit} loading={busy} color="gold">
          {mode === 'start' ? 'Start team' : mode === 'join' ? 'Join team' : 'Reserve a spot'}
        </Button>
      </Group>
    </Stack>
  );
};

const RulesCheck = ({ check }) => {
  if (!check) return null;
  return (
    <Stack gap="xs">
      {check.problems.length === 0 ? (
        <Alert color="green" variant="light" p="xs">
          <Text size="sm" style={{ textAlign: 'left' }}>
            ✓ {check.division} · average age {check.averageAge} · {check.size} dancers
          </Text>
        </Alert>
      ) : (
        <Alert color="red" variant="light" p="xs">
          {check.division && (
            <Text size="sm" weight={600} style={{ textAlign: 'left' }}>
              {check.division} · average age {check.averageAge}
            </Text>
          )}
          {check.problems.map(p => <Text key={p} size="sm" style={{ textAlign: 'left' }}>• {p}</Text>)}
        </Alert>
      )}
      {check.warnings.map(w => (
        <Alert key={w} color="yellow" variant="light" p="xs">
          <Text size="sm" style={{ textAlign: 'left' }}>{w}</Text>
        </Alert>
      ))}
    </Stack>
  );
};

/**
 * One ensemble entry in Step 3: a "join later" placeholder or a team.
 *
 * Props:
 *   entry          { entryId, slot, footwear, paid, team } from /api/ensemble
 *   index          1-based position among the dancer's ensemble entries
 *   registrationId the dancer's registration (music uploads go in its folder)
 *   onAction       (action, body) => Promise — calls /api/ensemble; throws Error(message)
 *   expanded, onToggle, readOnly
 */
const EnsembleCard = ({ entry, index, registrationId, onAction, expanded, onToggle, readOnly = false }) => {
  const team = entry.team;
  const isLead = Boolean(team?.isLead);
  const finalized = Boolean(team?.finalizedAt);
  const canEdit = isLead && !finalized && !readOnly;
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState({});

  // Load the piece details once per team. Every action refreshes `team`
  // (footwear, music upload, new code, someone joining); re-loading on each
  // would wipe what the lead has typed but not saved. Only the lead edits
  // these, so the local copy stays the source while the card is open.
  const teamId = team?.id;
  useEffect(() => {
    if (!team) return;
    setDetails({
      name: team.name || '',
      piece_title: team.piece_title || '',
      choreographed_by: team.choreographed_by || '',
      start_music: team.start_music || '',
      length: team.length || '',
    });
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, action, body = {}, confirm) => {
    if (confirm && !window.confirm(confirm)) return;
    setError(null);
    setBusy(key);
    try {
      await onAction(action, { ...body });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const missingDetails = team ? [
    !team.name && 'team name',
    !team.piece_title && 'piece title',
    !team.choreographed_by && 'choreographer / coach',
    !team.music_file_url && 'music',
    !team.start_music && 'music start (on/off stage)',
    !team.length && 'length',
  ].filter(Boolean) : [];
  const unpaid = team ? team.members.filter(m => !m.paid).map(m => m.name) : [];
  const readyToFinalize = team && missingDetails.length === 0 && unpaid.length === 0 && team.check?.eligible;

  const summary = !team
    ? 'Not on a team yet'
    : `${team.name} · ${team.members.length} dancer${team.members.length === 1 ? '' : 's'}${team.check?.division ? ` · ${team.check.division}` : ''}`;

  return (
    <Paper
      radius="md"
      p={{ base: 'sm', sm: 'lg' }}
      withBorder
      style={{ borderTop: '4px solid var(--primary-gold)', marginBottom: '1rem' }}
    >
      <UnstyledButton onClick={onToggle} aria-expanded={expanded} style={{ display: 'block', width: '100%', textAlign: 'left' }}>
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
          <div style={{ minWidth: 0 }}>
            <Group gap="sm" wrap="wrap" mb={4}>
              <Title order={2} size="h4" style={{ margin: 0, color: 'var(--charcoal)' }}>Ensemble {index}</Title>
              <Badge color={entry.paid ? 'green' : 'gray'} variant="light">{entry.paid ? 'Paid' : 'Not paid yet'}</Badge>
              {isLead && <Badge color="gold" variant="light">Team lead</Badge>}
              {finalized && <Badge color="green" variant="filled">Finalized</Badge>}
            </Group>
            <Text size="sm" weight={600} style={{ color: 'var(--charcoal)', overflowWrap: 'anywhere' }}>
              <IconUsers size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />{summary}
            </Text>
            <Text size="xs" color="dimmed">
              {team?.piece_title || 'Piece not set'} · Your footwear: {footwearLabel(entry.footwear)}
            </Text>
          </div>
          <IconChevronDown
            size={20}
            style={{ flexShrink: 0, marginTop: 4, color: 'var(--deep-gold)', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms ease' }}
          />
        </Group>
      </UnstyledButton>

      <Collapse in={expanded}>
        <Divider my="md" />
        {error && <Alert color="red" mb="md" p="xs"><Text size="sm" style={{ textAlign: 'left' }}>{error}</Text></Alert>}

        {/* Not on a team yet */}
        {!team && !readOnly && (
          <>
            <EnsembleJoinForm
              initialFootwear={entry.footwear}
              busy={Boolean(busy)}
              onStart={(name, footwear) => onAction('create', { entryId: entry.entryId, name, footwear })}
              onJoin={(code, footwear) => onAction('join', { entryId: entry.entryId, code, footwear })}
            />
            {!entry.paid && (
              <Group justify="flex-end" mt="sm">
                <Button variant="subtle" color="red" size="xs" loading={busy === 'delete'}
                  onClick={() => run('delete', 'deleteEntry', { entryId: entry.entryId }, 'Remove this ensemble entry?')}>
                  × Remove this ensemble entry
                </Button>
              </Group>
            )}
          </>
        )}

        {team && (
          <Stack gap="lg">
            {finalized ? (
              <Alert color="green" variant="light">
                <Text size="sm" style={{ textAlign: 'left' }}>
                  This team is finalized{team.division ? ` in the ${team.division}` : ''}. For any changes, email info@prixducanada.com.
                </Text>
              </Alert>
            ) : (
              <Paper p="sm" radius="md" style={{ background: 'var(--soft-cream)' }}>
                <Group justify="space-between" wrap="wrap" gap="xs">
                  <div style={{ textAlign: 'left' }}>
                    <Text size="xs" color="dimmed">Team code — share it with your teammates</Text>
                    <Text size="xl" weight={700} style={{ letterSpacing: 2, color: 'var(--deep-gold)' }}>{team.code}</Text>
                  </div>
                  <CopyButton value={inviteLink(team.code)}>
                    {({ copied, copy }) => (
                      <Button size="xs" variant="outline" onClick={copy} leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}>
                        {copied ? 'Link copied' : 'Copy invite link'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </Paper>
            )}

            <RulesCheck check={team.check} />

            {/* Members */}
            <div style={{ overflowX: 'auto' }}>
              <Table verticalSpacing="xs" fz="sm" striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Dancer</Table.Th>
                    <Table.Th>Age</Table.Th>
                    <Table.Th>Footwear</Table.Th>
                    <Table.Th>Paid</Table.Th>
                    {canEdit && <Table.Th />}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {team.members.map(m => (
                    <Table.Tr key={m.entryId}>
                      <Table.Td style={{ textAlign: 'left' }}>
                        {m.name}{' '}
                        {m.isYou && <Badge size="xs" variant="light">You</Badge>}{' '}
                        {m.isLead && <Badge size="xs" color="gold" variant="light">Lead</Badge>}
                      </Table.Td>
                      <Table.Td>{m.age ?? '—'}</Table.Td>
                      <Table.Td>{m.footwear === 'pointe' ? 'Pointe' : m.footwear === 'soft' ? 'Soft' : '—'}</Table.Td>
                      <Table.Td>{m.paid ? '✓' : 'Not yet'}</Table.Td>
                      {canEdit && (
                        <Table.Td>
                          {!m.isYou && (
                            <Tooltip label={`Remove ${m.name}`}>
                              <ActionIcon color="red" variant="subtle" loading={busy === `remove-${m.entryId}`}
                                onClick={() => run(`remove-${m.entryId}`, 'remove', { entryId: m.entryId }, `Remove ${m.name} from the team? Their entry (and any payment) stays with them.`)}>
                                <IconX size={16} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>

            {/* Your footwear */}
            {!finalized && !readOnly && (
              <Radio.Group
                label="Your footwear in this ensemble"
                value={entry.footwear || ''}
                onChange={(value) => run('footwear', 'setFootwear', { entryId: entry.entryId, footwear: value })}
                styles={inputStyles}
              >
                <Group mt="xs" gap="xl">
                  {FOOTWEAR_OPTIONS.map(o => <Radio key={o.value} value={o.value} label={o.label} disabled={busy === 'footwear'} />)}
                </Group>
              </Radio.Group>
            )}

            {/* Piece details — the lead edits, everyone sees */}
            <div>
              <Text size="sm" weight={600} mb={4} style={{ textAlign: 'left', color: 'var(--charcoal)' }}>Piece details</Text>
              {canEdit ? (
                <Stack gap="sm">
                  <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
                    No rush — everyone can pay now to hold their spots, and you can fill these in any time until
                    registration closes. They're needed before you finalize.
                  </Text>
                  <TextInput label="Team name" value={details.name || ''} onChange={(e) => setDetails(d => ({ ...d, name: e.target.value }))} styles={inputStyles} />
                  <TextInput label="Piece title" placeholder="e.g. Garland Waltz, Sleeping Beauty" value={details.piece_title || ''}
                    onChange={(e) => setDetails(d => ({ ...d, piece_title: e.target.value }))} styles={inputStyles} />
                  <TextInput label="Choreographer / Coach" placeholder="Your teacher's name" value={details.choreographed_by || ''}
                    onChange={(e) => setDetails(d => ({ ...d, choreographed_by: e.target.value }))} styles={inputStyles} />
                  <Select label="Length" placeholder="Select length" data={ENSEMBLE_LENGTHS} value={details.length || null}
                    description="3–5 dancers: up to 3 minutes. 6–8 dancers: up to 4 minutes (+1 minute with extended time)."
                    onChange={(v) => setDetails(d => ({ ...d, length: v || '' }))} styles={inputStyles} />
                  <Radio.Group label="Start music" description="Will the dancers be ON or OFF stage when the music begins?"
                    value={details.start_music || ''} onChange={(v) => setDetails(d => ({ ...d, start_music: v }))} styles={inputStyles}>
                    <Group mt="xs" gap="xl">
                      <Radio value="on_stage" label="ON Stage" />
                      <Radio value="off_stage" label="OFF Stage" />
                    </Group>
                  </Radio.Group>
                  <Group justify="flex-end">
                    <Button size="xs" variant="outline" loading={busy === 'details'}
                      onClick={() => run('details', 'update', { ensembleId: team.id, ...details })}>
                      Save details
                    </Button>
                  </Group>
                  <MusicUpload
                    registrationId={registrationId}
                    variationNumber={`ensemble_${entry.slot}`}
                    existingMusicUrl={team.music_file_url}
                    existingMusicFileName={team.music_file_name}
                    maxDurationSeconds={300}
                    onUploadComplete={(m) => run('music', 'update', { ensembleId: team.id, music_file_url: m.url, music_file_name: m.fileName })}
                    onUploadError={(err) => setError(err?.message || 'Music upload failed')}
                  />
                </Stack>
              ) : (
                <Stack gap={4}>
                  <Text size="sm" style={{ textAlign: 'left' }}><strong>Piece:</strong> {team.piece_title || '—'}</Text>
                  <Text size="sm" style={{ textAlign: 'left' }}><strong>Choreographer / Coach:</strong> {team.choreographed_by || '—'}</Text>
                  <Text size="sm" style={{ textAlign: 'left' }}><strong>Length:</strong> {team.length || '—'} · <strong>Music starts:</strong> {team.start_music === 'on_stage' ? 'On stage' : team.start_music === 'off_stage' ? 'Off stage' : '—'}</Text>
                  <Text size="sm" style={{ textAlign: 'left' }}><strong>Music:</strong> {team.music_file_name || (team.music_file_url ? 'Uploaded' : 'Not uploaded yet')}</Text>
                  {!finalized && (
                    <Text size="xs" color="dimmed" style={{ textAlign: 'left' }}>
                      Your team lead fills these in — they can be added after you pay, any time until registration closes.
                    </Text>
                  )}
                </Stack>
              )}
            </div>

            {/* Finalize — lead only */}
            {canEdit && (
              <Paper p="sm" radius="md" withBorder>
                <Text size="sm" weight={600} mb="xs" style={{ textAlign: 'left' }}>Finalize your team</Text>
                <Text size="xs" color="dimmed" mb="sm" style={{ textAlign: 'left' }}>
                  Finalizing locks the team and its details. After that, changes go through info@prixducanada.com.
                </Text>
                {!readyToFinalize && (
                  <Stack gap={2} mb="sm">
                    {missingDetails.length > 0 && <Text size="sm" style={{ textAlign: 'left' }}>• Add: {missingDetails.join(', ')}</Text>}
                    {unpaid.length > 0 && <Text size="sm" style={{ textAlign: 'left' }}>• Not paid yet: {unpaid.join(', ')}</Text>}
                    {team.check && !team.check.eligible && <Text size="sm" style={{ textAlign: 'left' }}>• The team doesn't meet the ensemble rules yet (see above)</Text>}
                  </Stack>
                )}
                <Button color="gold" disabled={!readyToFinalize} loading={busy === 'finalize'}
                  onClick={() => run('finalize', 'finalize', { ensembleId: team.id }, 'Finalize this team? It will be locked for registration.')}>
                  Finalize team
                </Button>
              </Paper>
            )}

            {/* Team actions */}
            {!finalized && !readOnly && (
              <Group justify="flex-end" gap="xs">
                {isLead ? (
                  <>
                    <Button size="xs" variant="subtle" loading={busy === 'code'}
                      onClick={() => run('code', 'newCode', { ensembleId: team.id }, 'Get a new team code? The old code and invite link will stop working.')}>
                      New code
                    </Button>
                    <Button size="xs" variant="subtle" color="red" loading={busy === 'disband'}
                      onClick={() => run('disband', 'disband', { ensembleId: team.id }, "Disband this team? Everyone's entry (and payment) stays with them as \"join later\".")}>
                      Disband team
                    </Button>
                  </>
                ) : (
                  <Button size="xs" variant="subtle" color="red" loading={busy === 'leave'}
                    onClick={() => run('leave', 'leave', { entryId: entry.entryId }, 'Leave this team? Your entry (and payment) stays with you as "join later".')}>
                    Leave team
                  </Button>
                )}
              </Group>
            )}
          </Stack>
        )}
      </Collapse>
    </Paper>
  );
};

export default EnsembleCard;
